import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Package, Stethoscope, type LucideIcon } from 'lucide-react';

import { brand } from '@brand';
import { useBootstrap, useRequiredIntakes } from '@/lib/api/hooks';
import { Drawer, Button } from '@/design-system/primitives';
import { Icon } from '@/lib/Icon';

/**
 * WelcomeScreen — intake-start drawer.
 *
 * Slides in from the right on desktop / up from the bottom on mobile,
 * mirroring system-web's `checkout/upsell-drawer.js` shell. Greets the
 * patient by first name, surfaces an estimated-time chip, and lays out
 * the three-step intake → review → fulfillment timeline. Bottom sticky
 * CTA hands off to the intake wizard at `/onboarding/intake`.
 */
export function WelcomeScreen() {
  const navigate = useNavigate();
  // Drawer is non-dismissible: this IS the route, not an overlay over content.
  // Without `hideClose`, ESC + backdrop would close it and the patient would
  // land on an empty page with no way back to start intake.
  const [open] = useState(true);
  const { data } = useBootstrap();
  const firstName = (data?.patient?.first_name ?? '').trim().toUpperCase();
  // Only explicit completed rows establish that the intake is on file.
  // An empty/loading list can precede a newly purchased order arriving.
  // The wizard still owns the remaining ID and consent requirements.
  const { data: required } = useRequiredIntakes();
  const intakeOnFile = Boolean(
    required?.length && required.every((i) => Boolean(i.intake_submitted_at)),
  );

  return (
    <Drawer
      open={open}
      hideClose
      title={intakeOnFile ? 'Finish account setup' : 'Patient Intake'}
      footer={
        <Button
          type="button"
          variant="filled"
          onClick={() => navigate('/onboarding/intake')}
          className="rounded-none"
        >
          <span>{intakeOnFile ? 'Continue' : 'Start intake'}</span>
          <span className="flex items-center justify-center w-8">
            <Icon name="Arrow" viewBox="0 0 10 12" className="w-8" />
          </span>
        </Button>
      }
    >
      {/* Greeting band */}
      <section className="px-15 pt-25 pb-20">
        <h1 className="type-headline-lg text-black">
          Hi {firstName || 'there'},
          <br />
          let&apos;s get you set up.
        </h1>
        <p className="mt-15 font-body text-15 text-[#7C7C7C] flex items-start gap-8">
          <span
            aria-hidden
            className="mt-5 shrink-0 inline-block w-10 text-[#7C7C7C]"
          >
            <Icon name="Arrow" viewBox="0 0 10 12" className="w-10 h-10" />
          </span>
          <span>
            {intakeOnFile
              ? 'Your intake is already on file. Continue to any remaining identity and consent steps before your clinician reviews your treatment.'
              : 'Your clinician needs a quick read of your medical history before prescribing anything.'}
          </span>
        </p>
      </section>

      {/* Estimated time chip — two-cell row */}
      {!intakeOnFile && (
        <div className="mx-15 mb-15 flex items-stretch bg-[#7C7C7C] text-white">
          <div className="flex-1 px-15 py-12 font-body text-15">
            Estimated time to complete
          </div>
          <div className="px-15 py-12 font-body text-15 bg-[#5E5E5E]">
            5 mins
          </div>
        </div>
      )}

      {/* Steps */}
      <ul className="px-15 pb-20 flex flex-col gap-10">
        <StepRow
          icon={ClipboardList}
          title={intakeOnFile ? 'Finish account setup' : 'Fill out your intake'}
          body={intakeOnFile
            ? 'Your medical questionnaire is complete. The next screen will guide you through any remaining setup steps.'
            : 'Basic contact info, goals, and health history. Save and come back any time to complete.'}
        />
        <StepRow
          icon={Stethoscope}
          title="Clinician review"
          badge="1–2 days"
          body="An assigned clinician reads your intake and reaches out with a recommended protocol."
        />
        <StepRow
          icon={Package}
          title="Shipped to you"
          body="Once approved, your order is placed and shipped within 1–2 days."
        />
      </ul>
    </Drawer>
  );
}

function StepRow({
  icon: StepIcon,
  title,
  body,
  badge,
}: {
  /** Used when the brand mark is not the thumbnail (brand.markStepThumbnails). */
  icon: LucideIcon;
  title: string;
  body: string;
  badge?: string;
}) {
  return (
    <li className="flex items-stretch border border-[#ECECEC]">
      {/* Placeholder thumbnail — swap for real artwork later. */}
      <div className="w-[6rem] shrink-0 bg-[#ECECEC] flex items-center justify-center text-[#7C7C7C]">
        {brand.markStepThumbnails ? (
          <div className="w-25">
            <Icon name="Mark Line" viewBox="0 0 32 30" />
          </div>
        ) : (
          <StepIcon aria-hidden size={24} strokeWidth={1.5} />
        )}
      </div>
      <div className="flex-1 min-w-0 px-15 py-15 flex flex-col gap-5">
        <div className="flex items-baseline gap-8 flex-wrap">
          <span className="type-headline-sm text-black">{title}</span>
          {badge && (
            <span className="font-mono text-10 uppercase text-[#7C7C7C]">
              [{badge}]
            </span>
          )}
        </div>
        <p className="font-body text-13 text-[#7C7C7C]">{body}</p>
      </div>
    </li>
  );
}
